import {isObservable, Observable, of, shareReplay, tap} from 'rxjs';

export class CacheByParams<Params extends unknown[], T, CacheKey = string | number> {
  private readonly cache: Map<CacheKey, T | Observable<T>> = new Map();

  constructor(
    private fetchFn: (...params: Params) => Observable<T>,
    private getCacheIdFromParams?: (...params: Params) => CacheKey,
  ) {}

  hasCachedItem(...params: Params): boolean {
    const cacheKey = this.getCacheId(...params);
    return this.cache.has(cacheKey);
  }

  get(...params: Params): Observable<T> {
    const cacheKey = this.getCacheId(...params);
    const cachedItem = this.cache.get(cacheKey);
    if (isObservable(cachedItem)) {
      return cachedItem;
    } else if (cachedItem !== undefined) {
      return of(cachedItem);
    } else {
      return this.fetchAndCache(cacheKey, params);
    }
  }

  private fetchAndCache(cacheKey: CacheKey, params: Params): Observable<T> {
    const fetch$ = this.fetchFn(...params).pipe(
      tap(item => this.cache.set(cacheKey, item ?? null)),
      shareReplay(1)
    );
    this.cache.set(cacheKey, fetch$);
    return fetch$;
  }

  private getCacheId(...params: Params): CacheKey {
    if (this.getCacheIdFromParams) {
      return this.getCacheIdFromParams(...params);
    } else {
      if (params.length > 0) {
        if (params.every(param => typeof param === 'number' || typeof param === 'string')) {
          return <any>(params.length === 1
            ? params[0]
            : params.join('_'));
        } else {
          throw new Error('Cache keys are not all type number or string');
        }
      } else {
        throw new Error('No parameters provided to cache over');
      }
    }
  }
}
